import { supabase } from './supabaseClient';

export const EXPENSE_CATEGORIES = [
  'Raw Materials & Ingredients',
  'Packaging',
  'Shipping & Fulfilment',
  'Website & Hosting',
  'Marketing & Advertising',
  'Software & Subscriptions',
  'Equipment & Tools',
  'Rent & Utilities',
  'Professional Fees (accounting/legal)',
  'Travel & Transport',
  'Bank & Payment Fees',
  'Wages & Contractors',
  'Registration & Licensing',
  'Other',
];

// Tag colors for the category pill in the expenses table, one per Skin Fairy
// JA brand color (14 brand colors, 14 categories — matched in the fixed
// order both lists are already defined in, not hand-picked per category).
// Each is a pastel tint + readable shade of the brand hex, not the raw brand
// hex itself, since several brand colors already do double duty elsewhere
// (Cream/Light Pink as page backgrounds, Warm/Light Orange as the CTA
// button) — reusing them flat here would either vanish into the page or be
// mistaken for a button. The category name is always shown as text next to
// the pill, so this is a scanning aid, not the sole way to tell categories
// apart.
export const CATEGORY_COLORS = {
  'Raw Materials & Ingredients': { bg: '#fdeef1', text: '#c0536a' }, // Brand Pink
  'Packaging': { bg: '#f6e9ed', text: '#8a4f61' }, // Muted Pink
  'Shipping & Fulfilment': { bg: '#f6e4e7', text: '#9c6b74' }, // Light Pink
  'Website & Hosting': { bg: '#f9e6e8', text: '#a56b71' }, // Very Light Pink
  'Marketing & Advertising': { bg: '#fbedf4', text: '#a94a78' }, // Medium Pink
  'Software & Subscriptions': { bg: '#ece6e7', text: '#3a2a2f' }, // Espresso
  'Equipment & Tools': { bg: '#f1e9d8', text: '#8a7550' }, // Cream
  'Rent & Utilities': { bg: '#f8f0e0', text: '#8a6d34' }, // Gold
  'Professional Fees (accounting/legal)': { bg: '#eef3ec', text: '#5b7a55' }, // Sage
  'Travel & Transport': { bg: '#f7ede4', text: '#96683f' }, // Warm Tan
  'Bank & Payment Fees': { bg: '#fdece3', text: '#b06a3f' }, // Peachy Tan
  'Wages & Contractors': { bg: '#fff0e2', text: '#b5651f' }, // Warm Orange
  'Registration & Licensing': { bg: '#fff2e6', text: '#b8712a' }, // Light Orange
  'Other': { bg: '#f2e4e3', text: '#6f4045' }, // Muted Mauve Brown
};

export const INCOME_CHANNELS = ['Website', 'In-Person', 'Wholesale'];

// Channels whose order numbers are auto-generated by a DB trigger
// (see supabase/migrations/0004_in_person_order_numbers.sql) rather than
// typed in — Website keeps its number from WooCommerce.
export const AUTO_ORDER_NUMBER_CHANNELS = ['In-Person', 'Wholesale'];
export const PAYMENT_METHODS = ['Bank Transfer', 'Cash', 'Card'];

// Which expense categories are direct costs of producing/delivering product
// (Cost of Goods Sold) rather than overhead. This split is what lets the P&L
// show a gross profit line at all; everything not listed here falls to
// operating expenses, so adding a new category defaults it to overhead.
export const COGS_CATEGORIES = [
  'Raw Materials & Ingredients',
  'Packaging',
  'Shipping & Fulfilment',
];

export const CIT_RATE = 0.25;
export const GCT_THRESHOLD_JMD = 15000000;

export const BALANCE_SECTIONS = ['Asset', 'Liability', 'Equity'];
export const BALANCE_SUBSECTIONS = ['Current', 'Non-current'];

export async function listExpenses() {
  const { data, error } = await supabase
    .from('expenses')
    .select('*')
    .order('entry_date', { ascending: false });
  if (error) throw error;
  return data;
}

export async function addExpense(entry) {
  const { data, error } = await supabase.from('expenses').insert(entry).select().single();
  if (error) throw error;
  return data;
}

export async function updateExpense(id, updates) {
  const { data, error } = await supabase
    .from('expenses')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteExpense(id) {
  const { error } = await supabase.from('expenses').delete().eq('id', id);
  if (error) throw error;
}

export async function listIncome() {
  const { data, error } = await supabase
    .from('income_entries')
    .select('*')
    .order('entry_date', { ascending: false });
  if (error) throw error;
  return data;
}

export async function addIncome(entry) {
  const { data, error } = await supabase.from('income_entries').insert(entry).select().single();
  if (error) throw error;
  return data;
}

export async function updateIncome(id, updates) {
  const { data, error } = await supabase
    .from('income_entries')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteIncome(id) {
  const { error } = await supabase.from('income_entries').delete().eq('id', id);
  if (error) throw error;
}

export async function listBalanceSheetItems() {
  const { data, error } = await supabase
    .from('balance_sheet_items')
    .select('*')
    .order('as_of_date', { ascending: false });
  if (error) throw error;
  return data;
}

export async function addBalanceSheetItem(item) {
  const { data, error } = await supabase
    .from('balance_sheet_items')
    .insert(item)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateBalanceSheetItem(id, updates) {
  const { data, error } = await supabase
    .from('balance_sheet_items')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteBalanceSheetItem(id) {
  const { error } = await supabase.from('balance_sheet_items').delete().eq('id', id);
  if (error) throw error;
}

const NUMBER_FORMAT = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Both JMD and USD render as a bare "$" via Intl's currency symbol, which is
// ambiguous once the two show up side by side in the same table. Prefix
// explicitly instead.
export function formatJMD(amount) {
  return `J$${NUMBER_FORMAT.format(amount || 0)}`;
}

export function formatUSD(amount) {
  return `US$${NUMBER_FORMAT.format(amount || 0)}`;
}

// Postgres `date` columns come back as bare "YYYY-MM-DD". Passing that to
// `new Date()` parses it as UTC midnight, which in Jamaica (UTC-5) is the
// *previous* day locally — so an entry dated the 1st of a month was landing
// in the previous month, and a 1 January entry in the previous year. A
// bookkeeping date has no time zone, so parse the parts as a local date.
export function parseEntryDate(value) {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function getYears(entries) {
  const years = new Set(entries.map((e) => parseEntryDate(e.entry_date).getFullYear()));
  years.add(new Date().getFullYear());
  return Array.from(years).sort((a, b) => b - a);
}

export function sumJmd(entries) {
  return entries.reduce((sum, e) => sum + Number(e.amount_jmd || 0), 0);
}

export function filterByYear(entries, year) {
  return entries.filter((e) => parseEntryDate(e.entry_date).getFullYear() === year);
}

export function rollingTwelveMonthTotal(entries, asOf = new Date()) {
  const cutoff = new Date(asOf);
  cutoff.setFullYear(cutoff.getFullYear() - 1);
  return sumJmd(entries.filter((e) => parseEntryDate(e.entry_date) > cutoff));
}
